import { TemplateResult, html, css, CSSResultGroup } from 'lit';
import { OutlineElement } from '../../../base/outline-element/outline-element';
import { customElement, property, state } from 'lit/decorators.js';
import componentStyles from './sic-breadcrumb.css.lit';
import componentVariables from './css-variables/vars-default.css.lit';
import { ResizeController } from '../../../controllers/resize-controller';
import { screenSizeMap } from '../../../controllers/screen-size-controller';
import { SlotsNGController as SlotController } from '../../../controllers/slots-ng-controller';

/**
 * The breadcrumb component
 *
 * @element sic-breadcrumb
 * @since 1.0.0
 * @slot breadcrumb - default slot for breadcrumb items
 * @slot home-icon - slot for the home icon
 * @slot back-icon - slot for the back icon
 * @prop {String} breadcrumb - The breadcrumb items to display in an unordered list.
 *
 */
@customElement('sic-breadcrumb')
export class SicSBreadcrumb extends OutlineElement {
  static styles: CSSResultGroup = [
    OutlineElement.styles,
    componentVariables,
    componentStyles,
    css``,
  ];
  slots = new SlotController(this);
  listItems: NodeListOf<HTMLLIElement>;
  resizeController: ResizeController;

  // The resizeController is initialized here because visibleBreakpoint is not set yet when constructor runs
  connectedCallback() {
    super.connectedCallback();
    this.resizeController = new ResizeController(this, {
      breakpoints: [screenSizeMap.get(this.visibleBreakpoint) as number],
      containerQuery: false,
    });
  }

  @state()
  private _breadcrumbAltered = false;

  /**
   * The breakpoint at which the secondary menu will be visible.
   *
   * This is done as a variable to ensure flexibility for future usages.
   */
  @property({ type: String, attribute: 'breakpoint' })
  visibleBreakpoint = 'lg';

  /**
   * If the `home-icon` slot has been populuated, this will return the icon element.
   *
   * @returns {Node | null} - The home icon element.
   */
  getHomeIcon = (): HTMLElement | null => {
    const homeIconExists = this.querySelectorAll('[slot="home-icon"]');
    // Check to prevent duplication
    if (homeIconExists.length <= 1) {
      const homeIconSlot =
        this.querySelector('[slot="home-icon"]')?.cloneNode(true);
      return homeIconSlot instanceof HTMLElement ? homeIconSlot : null;
    }
    return null;
  };

  /**
   * If the `back-icon` slot has been populuated, this will return the icon element.
   *
   * @returns {Node | null} - The back icon element.
   */
  getBackIcon = (): HTMLElement | null => {
    const backIconExists = this.querySelectorAll('[slot="back-icon"]');
    // Check to prevent duplication
    if (backIconExists.length <= 1) {
      const backIconSlot =
        this.querySelector('[slot="back-icon"]')?.cloneNode(true);
      return backIconSlot instanceof HTMLElement ? backIconSlot : null;
    }
    return null;
  };

  createBackIcon() {
    const backIconSlot = this.getBackIcon();
    if (!backIconSlot) return null;

    const iconElement = backIconSlot as HTMLElement;
    const backIcon = document.createElement('span');
    backIcon.classList.add('back-icon');
    backIcon.innerHTML = iconElement.outerHTML;
    return backIcon;
  }

  createHomeIcon() {
    const homeIconSlot = this.getHomeIcon();
    if (!homeIconSlot) return null;

    const iconElement = homeIconSlot as HTMLElement;
    const homeIcon = document.createElement('span');
    homeIcon.classList.add('home-icon');
    homeIcon.innerHTML = iconElement.outerHTML;
    return homeIcon;
  }

  applyHomeIcon(li: HTMLLIElement) {
    const homeIcon = this.getHomeIcon();
    if (homeIcon) {
      li.getElementsByTagName('a')[0].prepend(homeIcon);
    }
  }

  applyBackIcon(li: HTMLLIElement) {
    const backIcon = this.getBackIcon();
    if (backIcon) {
      li.getElementsByTagName('a')[0].prepend(backIcon);
    }
  }

  render(): TemplateResult {
    const list = this.getElementsByTagName('ol');
    const isMobile = this.resizeController.currentBreakpointRange === 0;

    const listItems = this.getElementsByTagName('li');
    const totalListItems = listItems.length;

    const backLink = listItems[totalListItems - 2].cloneNode(
      true
    ) as HTMLLIElement;

    this.applyBackIcon(backLink);

    if (!this._breadcrumbAltered && !isMobile) {
      Array.from(list).forEach((listElement: HTMLUListElement) => {
        const liElements = listElement.getElementsByTagName('li');
        Array.from(liElements).forEach(
          (listItemElement: HTMLLIElement, index: number) => {
            if (index === 0) {
              this.applyHomeIcon(listItemElement);
            }
          }
        );
      });
      this._breadcrumbAltered = true;
    }
    return isMobile
      ? html` <sic-container
          top-padding="none"
          bottom-padding="none"
          class="sic-breadcrumb-container is-mobile"
          ><ol>
            ${backLink}
          </ol></sic-container
        >`
      : html` <sic-container
          top-padding="none"
          bottom-padding="none"
          class="sic-breadcrumb-container"
          >${this.slots.conditionalSlot('breadcrumb', true)}</sic-container
        >`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'sic-breadcrumb': SicSBreadcrumb;
  }
}
